import { RxDocument } from 'rxdb';
import { defer, of } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import invariant from 'tiny-invariant';

import { PassengerBookingView } from '@mopla/data-models';
import { ITypedAction } from '@mopla/utils';

import {
	PassengerBookingActionTypes,
	removeBookingResult,
	resyncPassengerBookings,
	ResyncPassengerBookingsAction,
	TRemoveBookingAction,
} from '../actions/passengerBookingActions';
import { Effect } from '../business-logic';
import { ofType } from '../operators/ofType';

export const resyncPassengerBookingEffect: Effect<
	ResyncPassengerBookingsAction
> = (actions$, deps) =>
	actions$.pipe(
		ofType(PassengerBookingActionTypes.ReSyncBookings),
		concatMap(() => {
			deps.replications?.['passenger_booking'].reSync();
			return of(null);
		})
	);

export const removePassengerBookingEffect: Effect<TRemoveBookingAction> = (
	actions$,
	dependencies
) =>
	actions$.pipe(
		ofType(PassengerBookingActionTypes.RemoveBooking),
		concatMap((action: TRemoveBookingAction) =>
			defer(async () => {
				try {
					const bookingToRemove: RxDocument<PassengerBookingView> =
						await dependencies.db['passenger_booking']
							.findOne({
								selector: {
									bookingNumber: { $eq: action.payload.bookingNumber },
								},
							})
							.exec();

					invariant(
						bookingToRemove,
						'[Booking effects] trying to remove a booking which is not found in the local db'
					);

					await dependencies.api.delete(
						`/api/passengers/removeBooking?bookingId=${action.payload.id}`
					);

					actions$.next(resyncPassengerBookings() as ITypedAction<any, any>);

					return removeBookingResult();
				} catch (error: any) {
					dependencies.Sentry.captureException(error);
					return removeBookingResult({ error });
				}
			})
		)
	);
